import { useMemo, useState } from "react";
import api, { apiError, brl, parseMoney } from "@/lib/api";
import { toast } from "sonner";
import { Lock, Wand2 } from "lucide-react";
import { Modal } from "@/components/panel/ui";
import { Button, Field, Input, Textarea } from "@/components/ui-kit";

const METHOD_LABEL = {
  dinheiro: "Dinheiro (gaveta)",
  pix: "PIX",
  cartao_debito: "Cartão débito",
  cartao_credito: "Cartão crédito",
};

/** Onde cada forma é conferida — é o que o operador vai abrir para comparar. */
const METHOD_HINT = {
  dinheiro: "Conte as cédulas e moedas",
  pix: "Extrato do PIX no banco",
  cartao_debito: "Relatório da maquininha",
  cartao_credito: "Relatório da maquininha",
};

const ORDEM = ["dinheiro", "pix", "cartao_debito", "cartao_credito"];

/**
 * Fechamento do turno, com conferência de TODAS as formas de pagamento.
 *
 * Antes o fechamento era um card fixo na lateral da tela, e conferia só a
 * gaveta. Dois problemas moravam aí. O primeiro: PIX, débito e crédito saíam do
 * turno sem ninguém comparar com nada, e a diferença só aparecia dias depois, na
 * conciliação, quando já não dava para saber de que dia era. O segundo: sendo um
 * card sempre visível, ele disputava espaço com o extrato e ainda assim não
 * chamava para a ação — fechar caixa é um ato deliberado, de fim de turno, e
 * merece a tela inteira por um instante.
 *
 * A diferença aparece POR FORMA enquanto se digita, e não só no total: "faltam
 * R$ 40" não diz o que investigar; "faltam R$ 40 no dinheiro e sobram R$ 40 no
 * débito" é uma venda registrada na forma errada, e o operador resolve na hora.
 */
export default function CloseCashDialog({ expectedByMethod, onClose, onDone }) {
  const esperado = useMemo(
    () => ORDEM.filter((m) => expectedByMethod && m in expectedByMethod),
    [expectedByMethod],
  );
  const [contado, setContado] = useState({});
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  const valorDe = (m) => parseMoney(contado[m] ?? "");
  const difDe = (m) => round2(valorDe(m) - (expectedByMethod[m] || 0));
  const preenchido = (m) => (contado[m] ?? "") !== "";

  // Só conta como conferido o que foi digitado. Um campo em branco vale 0 no
  // envio, mas não pode ser lido como "conferi e deu zero" antes disso.
  const totalEsperado = round2(esperado.reduce((s, m) => s + (expectedByMethod[m] || 0), 0));
  const totalContado = round2(esperado.reduce((s, m) => s + valorDe(m), 0));
  const totalDif = round2(totalContado - totalEsperado);
  const faltaConferir = esperado.filter((m) => !preenchido(m));

  /** Preenche tudo com o esperado — o turno que bateu certo é a maioria. */
  const baterTudo = () => setContado(
    Object.fromEntries(esperado.map((m) => [m, String((expectedByMethod[m] || 0).toFixed(2))])),
  );

  const confirmar = async () => {
    setBusy(true);
    try {
      await api.post("/pos/cash-session/close", {
        counted: Object.fromEntries(esperado.map((m) => [m, valorDe(m)])),
        notes,
      });
      toast.success("Caixa fechado");
      onDone();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <Modal open onClose={onClose} title="Fechar caixa" maxWidth="max-w-xl" testId="cash-close-dialog">
      <p className="text-sm text-slate-600">
        Confira cada forma de pagamento do turno. A diferença fica registrada no histórico, no seu nome.
      </p>

      <div className="flex justify-end mt-3">
        <Button variant="link" size="sm" icon={Wand2} onClick={baterTudo} data-testid="cash-close-fill">
          Preencher com o esperado
        </Button>
      </div>

      <div className="mt-1 border border-slate-200 rounded-xl divide-y divide-slate-100">
        {esperado.map((m) => {
          const dif = difDe(m);
          const mostrarDif = preenchido(m) && Math.abs(dif) > 0.001;

          return (
            <div key={m} className="flex items-center gap-3 px-3 py-2.5" data-testid="cash-close-method">
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-slate-900">{METHOD_LABEL[m] || m}</p>
                <p className="text-xs text-slate-400">{METHOD_HINT[m]}</p>
              </div>
              <div className="text-right shrink-0">
                <p className="text-[11px] uppercase tracking-wider text-slate-400">Esperado</p>
                <p className="font-mono text-sm text-slate-600 tabular-nums">{brl(expectedByMethod[m] || 0)}</p>
              </div>
              <div className="w-28 shrink-0">
                <Input inputMode="decimal" mono size="sm" placeholder="0,00"
                  value={contado[m] ?? ""}
                  onChange={(e) => setContado((c) => ({ ...c, [m]: e.target.value }))}
                  aria-label={`Valor contado em ${METHOD_LABEL[m] || m}`}
                  data-testid={`cash-close-counted-${m}`}
                  className="text-right" />
                {mostrarDif && (
                  <p className={`text-[11px] font-mono text-right mt-0.5 ${dif < 0 ? "text-red-600" : "text-amber-600"}`}
                    data-testid={`cash-close-diff-${m}`}>
                    {dif < 0 ? "falta " : "sobra "}{brl(Math.abs(dif))}
                  </p>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <div className="flex items-center justify-between gap-3 mt-3 px-3 py-2.5 rounded-xl bg-slate-50 border border-slate-200">
        <span className="text-sm font-semibold text-slate-700">Diferença do turno</span>
        <span className={`font-mono tabular-nums font-semibold ${
          Math.abs(totalDif) < 0.005 ? "text-emerald-700" : totalDif < 0 ? "text-red-600" : "text-amber-600"}`}
          data-testid="cash-close-total-diff">
          {Math.abs(totalDif) < 0.005 ? "Bateu certo" : `${totalDif < 0 ? "−" : "+"} ${brl(Math.abs(totalDif))}`}
        </span>
      </div>

      {/* Aviso, não trava: às vezes a maquininha não abre na hora do fechamento
          e prender o turno por isso deixaria o caixa aberto a noite inteira. */}
      {faltaConferir.length > 0 && (
        <p className="text-xs text-amber-700 mt-2" data-testid="cash-close-pending">
          Sem conferir: {faltaConferir.map((m) => METHOD_LABEL[m]).join(", ")} — vai como R$ 0,00.
        </p>
      )}

      <Field label="Observações" className="mt-4">
        <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2}
          placeholder="Divergência, ocorrência do turno..." data-testid="cash-close-notes" />
      </Field>

      <div className="flex flex-col-reverse sm:flex-row gap-2 sm:justify-end mt-5">
        <Button variant="secondary" onClick={onClose} data-testid="cash-close-back">Voltar</Button>
        <Button icon={Lock} loading={busy} onClick={confirmar} data-testid="cash-close-confirm">
          Fechar caixa
        </Button>
      </div>
    </Modal>
  );
}

const round2 = (n) => Math.round(n * 100) / 100;
